import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IGaiaObjectsState } from "./definitions";

const initialState: IGaiaObjectsState = {
  gaiaObjectId: "",
  taskList: [],
  taskGroupRatios: [],
  assessmentList: [],
  paymentList: [],
  selectedGaiaObject: {},
  teamName: "",
  selectedTask: {},
  selectedAssessment: {
    tax: "",
    penalty: "",
    interest: "",
    other: "",
    creatingPaymentFromAssessment: false,
    taxAreaBreakdowns: [],
    taxYearBreakdowns: [],
  },
};

export const gaiaObjectsSlice = createSlice({
  name: "gaiaObjects",
  initialState,
  reducers: {
    setGaiaObjectId: (state, action) => {
      state.gaiaObjectId = action.payload;
    },
    removeSelectedGaiaObject: (state) => {
      state.selectedGaiaObject = {};
    },
    getGaiaObjectById: (state, action) => {
      return {
        ...state,
        selectedGaiaObject: action.payload,
      };
    },
    addAssessmentsToList: (state, action) => {
      return {
        ...state,
        assessmentList: [...state.assessmentList, ...action.payload],
      };
    },
    deleteSingleAssessmentFromList: (state, action) => {
      return {
        ...state,
        assessmentList: state.assessmentList.filter(
          (assessment) => assessment.assessmentId !== action.payload
        ),
      };
    },
    setAssessmentList: (state, action) => {
      return {
        ...state,
        assessmentList: [...action.payload],
      };
    },
    updateSingleAssessment: (state, action) => {
      return {
        ...state,
        assessmentList: state.assessmentList.map((assessment) => {
          if (assessment.assessmentId === action.payload.assessmentId) {
            return { ...assessment, ...action.payload };
          }
          return assessment;
        }),
      };
    },
    addPaymentsToList: (state, action) => {
      return {
        ...state,
        paymentList: [...state.paymentList, ...action.payload],
      };
    },
    deleteSinglePaymentFromList: (state, action) => {
      return {
        ...state,
        paymentList: state.paymentList.filter(
          (payment) => payment.paymentId !== action.payload
        ),
      };
    },
    setPaymentList: (state, action) => {
      return {
        ...state,
        paymentList: [...action.payload],
      };
    },
    updateSinglePayment: (state, action) => {
      return {
        ...state,
        paymentList: state.paymentList.map((payment) => {
          if (payment.paymentId === action.payload.paymentId) {
            return { ...payment, ...action.payload };
          }
          return payment;
        }),
      };
    },
    addTasksToList: (state, action) => {
      return { ...state, taskList: [...state.taskList, ...action.payload] };
    },
    deleteSingleTaskFromList: (state, action) => {
      return {
        ...state,
        taskList: state.taskList.filter(
          (task) => task.taskId !== action.payload
        ),
      };
    },
    removeTasksFromList: (state, action) => {
      return {
        ...state,
        taskList: state.taskList.filter(
          (task) => task.parentTaskId !== action.payload
        ),
      };
    },
    updateSingleTask: (state, action) => {
      return {
        ...state,
        taskList: state.taskList.map((task) => {
          if (task.taskId === action.payload.taskId) {
            return { ...task, ...action.payload };
          }
          return task;
        }),
      };
    },
    teamNameUpdated: (state, action: PayloadAction<string>) => {
      state.teamName = action.payload;
    },
    removeSelectedTask: (state) => {
      state.selectedTask = {};
    },
    getTaskById: (state, action) => {
      return {
        ...state,
        selectedTask: action.payload,
      };
    },
    setSelectedAssessment: (state, action) => {
      return {
        ...state,
        selectedAssessment: {
          tax: action.payload.tax || "",
          penalty: action.payload.penalty || "",
          interest: action.payload.interest || "",
          other: action.payload.other || "",
          creatingPaymentFromAssessment:
            action.payload.creatingPaymentFromAssessment || false,
          taxAreaBreakdowns: action.payload.taxAreaBreakdowns,
          taxYearBreakdowns: action.payload.taxYearBreakdowns,
        },
      };
    },
    resetSelectedAssessment: (state) => {
      return {
        ...state,
        selectedAssessment: {
          tax: "",
          penalty: "",
          interest: "",
          other: "",
          creatingPaymentFromAssessment: false,
          taxAreaBreakdowns: [],
          taxYearBreakdowns: [],
        },
      };
    },
  },
});

export const {
  setGaiaObjectId,
  removeSelectedGaiaObject,
  getGaiaObjectById,
  teamNameUpdated,
  addTasksToList,
  removeTasksFromList,
  deleteSingleTaskFromList,
  updateSingleTask,
  getTaskById,
  removeSelectedTask,
  addAssessmentsToList,
  deleteSingleAssessmentFromList,
  setAssessmentList,
  updateSingleAssessment,
  addPaymentsToList,
  deleteSinglePaymentFromList,
  setPaymentList,
  updateSinglePayment,
  setSelectedAssessment,
  resetSelectedAssessment,
} = gaiaObjectsSlice.actions;

export default gaiaObjectsSlice.reducer;
